import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import {
  ONBOARDING_EVENT,
  V2_AGENT_EVENT,
  messageLengthBucket,
  capture,
  clearIdentifiedSession,
  LOGGED_OUT_EVENT,
} from '../../analytics/posthog';
import styles from './V2OnboardingAgent.module.css';
import { getFirstNameFromStorage } from '../../utils/helper';
import config, { logger } from '../../config';
import { getOnboardingAIData, getApiHeaders, updateV2OnboardingAiResult } from '../../utils/api';
import FinancialProfilePanel, {
  FINANCIAL_PROFILE_DEFAULTS,
  getV2OnboardingAiResultFromResponse,
  pickFinancialProfileFromV2Result,
} from './FinancialProfilePanel';
import {
  RELATIVE_TIME_UPDATE_MS,
  MOBILE_MQ,
  awaitMinTypingDotsDisplay,
  isPlainObject,
  stripDefaultPrefilledAnswers,
  getMandatoryProfileCompletionPercent,
  buildAgentMessage,
  buildUserMessage,
  getLastAgentMessageId,
  getV2OnboardingApiChat,
  getV2OnboardingDone,
  lastV2OnboardingApiChatEntry,
  parseProto3ApiChat,
  serializeChatForApi,
  getLastAgentQuestionFromUiMessages,
  withComposerFocusAfterRestore,
  getProto3ApproximateLocationShared,
  postProto3Turn,
  postProto3Start,
  turnPatchForGuard,
  applyFinancialProfileFromTurn,
  sanitizeAgentReply,
  cx,
} from './V2OnboardingAgent/helpers';
import { useProfileDrawer } from './V2OnboardingAgent/useProfileDrawer';
import {
  V2ChatHeader,
  V2LoadingPanel,
  V2TypingDots,
  LocationConsentPopup,
  TopAppHeader,
  V2ErrorState,
  V2MessageList,
  V2Composer,
  ProfileDrawer,
} from './V2OnboardingAgent/ChatUI';

/**
 * @param {{ variant?: 'onboarding' | 'copilot', topBar?: import('react').ReactNode, showScenarioCta?: boolean }} props
 * - onboarding: full flow; when complete, shows View Results and locks the composer.
 * - copilot: same chat + profile UI inside the dashboard; no View Results; composer stays available after onboarding_ai_done.
 */
export default function V2OnboardingAgent({ variant = 'onboarding', topBar = null, showScenarioCta = true } = {}) {
  const isCopilotMode = variant === 'copilot';
  const variantLabel = isCopilotMode ? 'copilot' : 'onboarding';
  const showTopAppHeader = !isCopilotMode;
  const navigate = useNavigate();
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;
  const captureV2 = useCallback(
    (client, props) => capture(client, V2_AGENT_EVENT, { ...props, variant: variantLabel }),
    [variantLabel]
  );

  const messagesContainerRef = useRef(null);
  const messagesInnerRef = useRef(null);
  const composerApiRef = useRef(null);
  const pageRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [financialProfileRaw, setFinancialProfileRaw] = useState(null);
  const [done, setDone] = useState(false);
  const [onboardingAiDone, setOnboardingAiDone] = useState(false);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [awaitingProto3Start, setAwaitingProto3Start] = useState(false);
  const typingDotsShownAtRef = useRef(0);
  const lastAgentMessageIdAtThinkingStartRef = useRef(null);
  const [relativeTimeTick, setRelativeTimeTick] = useState(0);

  const clientLocationRef = useRef(null);
  const locationConsentGrantedRef = useRef(false);
  const locationConsentResolverRef = useRef(null);
  const [showLocationConsent, setShowLocationConsent] = useState(false);
  /** Closed until we know a first-run location prompt will not cover the chat. */
  const [holdProfileAutoOpen, setHoldProfileAutoOpen] = useState(true);

  const hydrateClientLocation = useCallback(async (maxWaitMs = 1200) => {
    if (!locationConsentGrantedRef.current) return clientLocationRef.current;
    if (clientLocationRef.current) return clientLocationRef.current;
    const locPromise = getProto3ApproximateLocationShared();
    const loc = await Promise.race([
      locPromise,
      new Promise((resolve) => {
        window.setTimeout(() => resolve(null), maxWaitMs);
      }),
    ]);
    if (loc) {
      clientLocationRef.current = loc;
      return loc;
    }
    locPromise.then((lateLoc) => {
      if (lateLoc) clientLocationRef.current = lateLoc;
    });
    return clientLocationRef.current;
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const isAgentThinking = saving || awaitingProto3Start;

  const markThinkingStart = useCallback(() => {
    typingDotsShownAtRef.current = Date.now();
    lastAgentMessageIdAtThinkingStartRef.current = getLastAgentMessageId(messagesRef.current);
  }, []);

  useEffect(() => {
    if (!isAgentThinking) return;
    markThinkingStart();
  }, [isAgentThinking, markThinkingStart]);

  const latestAgentMessageId = getLastAgentMessageId(messages);
  const hasNewAgentReplySinceThinking =
    latestAgentMessageId != null && latestAgentMessageId !== lastAgentMessageIdAtThinkingStartRef.current;
  const showTypingDots = isAgentThinking && !hasNewAgentReplySinceThinking;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRelativeTimeTick((prev) => prev + 1);
    }, RELATIVE_TIME_UPDATE_MS);
    return () => window.clearInterval(intervalId);
  }, []);

  useLayoutEffect(() => {
    if (!ready) return undefined;
    const scrollEl = messagesContainerRef.current;
    const inner = messagesInnerRef.current;
    if (!scrollEl || !inner) return undefined;

    const snap = () => {
      scrollEl.scrollTop = scrollEl.scrollHeight;
    };

    snap();
    const id = requestAnimationFrame(() => {
      snap();
      requestAnimationFrame(snap);
    });

    const ro = new ResizeObserver(() => {
      snap();
    });
    ro.observe(inner);

    return () => {
      cancelAnimationFrame(id);
      ro.disconnect();
    };
  }, [messages, ready]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const pageEl = pageRef.current;
    if (!pageEl) return undefined;

    const mobileMq = window.matchMedia(MOBILE_MQ);
    const vv = window.visualViewport;
    const KEYBOARD_GAP_PX = 100;
    const isStandalonePage = () => pageEl.classList.contains(styles['v2-page--standalone']);

    const clearVvVars = () => {
      pageEl.style.removeProperty('--v2-vv-height');
      pageEl.style.removeProperty('--v2-vv-offset-top');
      pageEl.classList.remove(styles['v2-page--keyboard-open']);
      document.documentElement.classList.remove('v2-onboarding-keyboard-open');
      document.body.classList.remove('v2-onboarding-keyboard-open');
    };

    const syncVisualViewport = () => {
      if (!mobileMq.matches || !vv || !isStandalonePage()) {
        clearVvVars();
        return;
      }

      const height = Math.round(vv.height);
      const offsetTop = Math.round(vv.offsetTop);
      pageEl.style.setProperty('--v2-vv-height', `${height}px`);
      pageEl.style.setProperty('--v2-vv-offset-top', `${offsetTop}px`);

      const keyboardOpen = window.innerHeight - height > KEYBOARD_GAP_PX || offsetTop > 0;
      pageEl.classList.toggle(styles['v2-page--keyboard-open'], keyboardOpen);
      document.documentElement.classList.toggle('v2-onboarding-keyboard-open', keyboardOpen);
      document.body.classList.toggle('v2-onboarding-keyboard-open', keyboardOpen);

      if (keyboardOpen) {
        const scrollEl = messagesContainerRef.current;
        if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    };

    syncVisualViewport();
    if (vv) {
      vv.addEventListener('resize', syncVisualViewport);
      vv.addEventListener('scroll', syncVisualViewport);
    }
    window.addEventListener('resize', syncVisualViewport);
    if (typeof mobileMq.addEventListener === 'function') {
      mobileMq.addEventListener('change', syncVisualViewport);
    } else {
      mobileMq.addListener(syncVisualViewport);
    }

    return () => {
      clearVvVars();
      if (vv) {
        vv.removeEventListener('resize', syncVisualViewport);
        vv.removeEventListener('scroll', syncVisualViewport);
      }
      window.removeEventListener('resize', syncVisualViewport);
      if (typeof mobileMq.removeEventListener === 'function') {
        mobileMq.removeEventListener('change', syncVisualViewport);
      } else {
        mobileMq.removeListener(syncVisualViewport);
      }
    };
  }, [ready, loadError, isCopilotMode]);

  const requestLocationConsent = useCallback(
    (signal) =>
      new Promise((resolve) => {
        if (signal?.aborted) {
          resolve(false);
          return;
        }

        locationConsentResolverRef.current = (choice) => {
          locationConsentResolverRef.current = null;
          resolve(Boolean(choice));
        };
        setShowLocationConsent(true);

        if (signal) {
          const onAbort = () => {
            setShowLocationConsent(false);
            if (locationConsentResolverRef.current) {
              const r = locationConsentResolverRef.current;
              locationConsentResolverRef.current = null;
              r(false);
            }
            signal.removeEventListener('abort', onAbort);
          };
          signal.addEventListener('abort', onAbort, { once: true });
        }
      }),
    []
  );

  const handleLocationConsentChoice = useCallback(
    (choice) => {
      captureV2(posthog, {
        action: 'location_consent',
        granted: Boolean(choice),
      });
      setShowLocationConsent(false);
      if (locationConsentResolverRef.current) {
        const r = locationConsentResolverRef.current;
        locationConsentResolverRef.current = null;
        r(choice);
      }
    },
    [posthog, captureV2]
  );

  const saveToApi = useCallback(async (uiMsgs, completed = false) => {
    const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN) || '';
    const chat = serializeChatForApi(uiMsgs);
    const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.V2_ONBOARDING_AI_UPDATE}`, {
      method: 'PUT',
      headers: getApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        token,
        v2_onboarding_ai: { chat, last_updated: Date.now() },
        onboarding_ai_done: completed,
      }),
    });
    if (!response.ok) {
      logger.error('V2 save failed:', response.status);
    }
  }, []);

  const handleFinancialProfilePersisted = useCallback(
    async (data) => {
      if (!data?.onboarding_ai_done) return;
      captureV2(posthog, { action: 'profile_completed_via_panel' });
      const msg = data.completion_message;
      const snippet = 'onboarding profile is complete';
      setOnboardingAiDone(true);
      setDone(!isCopilotMode);
      const already = messagesRef.current.some(
        (m) =>
          m.role === 'agent' &&
          typeof m.content === 'string' &&
          m.content.toLowerCase().includes(snippet)
      );
      if (msg && !already) {
        setMessages([...messagesRef.current, buildAgentMessage(msg, Date.now(), { animateTyping: true })]);
      }
    },
    [isCopilotMode, posthog, captureV2]
  );

  const persistV2OnboardingAiResult = useCallback(async (token, financialProfile) => {
    if (!token) return false;
    const result = await updateV2OnboardingAiResult(
      {
        token,
        v2OnboardingAiResult: {
          financial_profile: financialProfile,
          updated_at: Date.now(),
        },
      },
      navigate
    );
    if (!result.ok) {
      logger.warn('Failed to seed V2 onboarding result:', result.message || 'Unknown error');
      return false;
    }
    return true;
  }, [navigate]);

  const applyTurnToChat = useCallback(
    async (turn, baseMessages) => {
      const finalReply = sanitizeAgentReply(turn.reply, turnPatchForGuard(turn));
      const agentMsg = finalReply
        ? buildAgentMessage(finalReply, Date.now(), { animateTyping: true, focusComposerAfter: true })
        : null;
      if (agentMsg) {
        await awaitMinTypingDotsDisplay(typingDotsShownAtRef.current);
      }
      const withAgent = agentMsg ? [...baseMessages, agentMsg] : baseMessages;
      setMessages(withAgent);
      applyFinancialProfileFromTurn(turn, setUserAnswers, setFinancialProfileRaw);
      setDone(turn.completed && !isCopilotMode);
      setOnboardingAiDone(turn.completed);
      await saveToApi(withAgent, turn.completed);
      return { withAgent, finalReply };
    },
    [isCopilotMode, saveToApi]
  );

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;

    const run = async () => {
      try {
        const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN);
        if (!token) {
          if (!signal.aborted) {
            captureV2(posthogRef.current, {
              action: 'session_load_failed',
              reason: 'no_token',
            });
            setHoldProfileAutoOpen(false);
            setLoadError(true);
            setReady(true);
          }
          return;
        }

        const data = await getOnboardingAIData(token, navigate, false, signal);
        if (signal.aborted) return;

        const apiChat = getV2OnboardingApiChat(data);
        const tokenStr = String(token);
        const { uiMessages } = parseProto3ApiChat(apiChat);
        const isNewOnboarding = uiMessages.length === 0;

        if (isNewOnboarding) {
          setHoldProfileAutoOpen(true);
          const consented = await requestLocationConsent(signal);
          if (signal.aborted) return;
          setHoldProfileAutoOpen(false);
          locationConsentGrantedRef.current = consented;
          if (consented) {
            await hydrateClientLocation(4500);
          } else {
            clientLocationRef.current = null;
          }
        } else {
          locationConsentGrantedRef.current = false;
          clientLocationRef.current = null;
          if (!signal.aborted) setHoldProfileAutoOpen(false);
        }

        const apiV2Result = getV2OnboardingAiResultFromResponse(data);
        const profileFromApi = pickFinancialProfileFromV2Result(apiV2Result);
        const shouldSeedDefaults = apiV2Result == null || profileFromApi == null;
        const seedProfile = shouldSeedDefaults ? { ...FINANCIAL_PROFILE_DEFAULTS } : profileFromApi;

        if (shouldSeedDefaults) {
          await persistV2OnboardingAiResult(tokenStr, seedProfile);
        }
        if (signal.aborted) return;

        const apiDone = getV2OnboardingDone(data);
        const lastApiEntry = lastV2OnboardingApiChatEntry(data);
        const shouldResumeFromLastAnswer =
          !signal.aborted &&
          !apiDone &&
          uiMessages.length > 0 &&
          lastApiEntry?.type === 'answer' &&
          typeof lastApiEntry?.text === 'string' &&
          lastApiEntry.text.trim().length > 0;

        const restoredMessages = withComposerFocusAfterRestore(uiMessages, {
          onboardingDone: apiDone,
          resumeFromLastAnswer: shouldResumeFromLastAnswer,
        });

        if (!signal.aborted) {
          setMessages(restoredMessages);
          setUserAnswers((prev) => ({ ...prev, ...stripDefaultPrefilledAnswers(seedProfile) }));
          setFinancialProfileRaw(seedProfile);
          setOnboardingAiDone(apiDone);
          setDone(isCopilotMode ? false : apiDone);
        }

        if (shouldResumeFromLastAnswer) {
          setSaving(true);
          try {
            await hydrateClientLocation(900);
            const turn = await postProto3Turn({
              token: tokenStr,
              message: lastApiEntry.text.trim(),
              client_location: clientLocationRef.current,
              continuous_copilot_chat: isCopilotMode,
              last_ai_question: getLastAgentQuestionFromUiMessages(uiMessages),
              signal,
            });

            if (turn.ok && turn.reply) {
              await applyTurnToChat(turn, uiMessages);
            } else if (turn.ok && turn.completed) {
              setDone(!isCopilotMode);
              setOnboardingAiDone(true);
              await saveToApi(uiMessages, true);
            }
          } catch (e) {
            if (e?.name === 'AbortError' || signal.aborted) return;
            logger.error('Proto3 resume-after-error failed:', e);
          } finally {
            if (!signal.aborted) setSaving(false);
          }
        }

        if (!uiMessages.length) {
          if (signal.aborted) return;
          setAwaitingProto3Start(true);
          await new Promise((resolve) => {
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(resolve);
            });
          });
          if (signal.aborted) {
            setAwaitingProto3Start(false);
            return;
          }
          try {
            await hydrateClientLocation(1200);
            if (signal.aborted) return;
            const { res, body, reply } = await postProto3Start({
              token: tokenStr,
              client_location: clientLocationRef.current,
              signal,
            });
            if (signal.aborted) return;
            if (res?.ok && reply) {
              await awaitMinTypingDotsDisplay(typingDotsShownAtRef.current);
              const next = [buildAgentMessage(reply, Date.now(), { animateTyping: true, focusComposerAfter: true })];
              setMessages(next);
              await saveToApi(next, false);
              const fpStart = body?.data?.financial_profile;
              if (isPlainObject(fpStart) && Object.keys(fpStart).length) {
                const meaningful = stripDefaultPrefilledAnswers(fpStart);
                if (Object.keys(meaningful).length) {
                  setUserAnswers((prev) => ({ ...prev, ...meaningful }));
                }
              }
            } else if (!signal.aborted) {
              captureV2(posthogRef.current, {
                action: 'proto3_start_failed',
                status: res?.status ?? 0,
                had_reply: Boolean(reply),
              });
            }
          } catch (e) {
            if (e?.name === 'AbortError' || signal.aborted) return;
            logger.error('Proto3 start failed:', e);
          } finally {
            if (!signal.aborted) setAwaitingProto3Start(false);
          }
        }

        if (!signal.aborted) {
          captureV2(posthogRef.current, {
            action: 'session_loaded',
            initial_ui_message_count: uiMessages.length,
            onboarding_ai_done: getV2OnboardingDone(data),
            new_session: isNewOnboarding,
            resume_last_answer_attempted: shouldResumeFromLastAnswer,
          });
          setReady(true);
        }
      } catch (e) {
        if (e?.name === 'AbortError' || signal.aborted) return;
        logger.error('V2 load error:', e);
        captureV2(posthogRef.current, {
          action: 'session_load_failed',
          reason: 'error',
        });
        setHoldProfileAutoOpen(false);
        setLoadError(true);
        setReady(true);
      }
    };

    run();
    return () => ac.abort();
  }, [
    navigate,
    saveToApi,
    persistV2OnboardingAiResult,
    requestLocationConsent,
    hydrateClientLocation,
    isCopilotMode,
    captureV2,
    applyTurnToChat,
  ]);

  const composerLocked = done && !isCopilotMode;

  const focusComposerInput = useCallback(() => {
    composerApiRef.current?.focus?.();
  }, []);

  const handleAgentTypingComplete = useCallback(
    (messageId) => {
      const lastAgent = [...messagesRef.current].reverse().find((m) => m?.role === 'agent');
      if (!lastAgent?.focusComposerAfter || lastAgent.id !== messageId) return;
      focusComposerInput();
    },
    [focusComposerInput]
  );

  useEffect(() => {
    if (!ready || loadError || showLocationConsent) return;
    if (composerLocked || saving || composerApiRef.current?.listening) return;

    const msgs = messagesRef.current;
    if (!msgs.length) return;
    const last = msgs[msgs.length - 1];
    const shouldFocus =
      last?.role === 'user' ||
      (last?.role === 'agent' && last.focusComposerAfter);
    if (!shouldFocus) return;

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => focusComposerInput());
    });
    return () => cancelAnimationFrame(id);
  }, [ready, loadError, showLocationConsent, composerLocked, saving, messages, focusComposerInput]);

  const handleSubmitText = async (text) => {
    captureV2(posthog, {
      action: 'message_send',
      length_bucket: messageLengthBucket(text.length),
    });

    const lastAiQuestion = getLastAgentQuestionFromUiMessages(messagesRef.current);
    const nextMessages = [...messagesRef.current, buildUserMessage(text)];
    setMessages(nextMessages);
    markThinkingStart();
    setSaving(true);

    try {
      const token = localStorage.getItem(config.STORAGE_KEYS.TOKEN) || '';
      if (!token) {
        navigate('/login');
        return;
      }
      await hydrateClientLocation(900);

      const turn = await postProto3Turn({
        token,
        message: text,
        client_location: clientLocationRef.current,
        continuous_copilot_chat: isCopilotMode,
        last_ai_question: lastAiQuestion,
      });

      const { finalReply } = await applyTurnToChat(turn, nextMessages);
      captureV2(posthog, {
        action: 'turn_received',
        onboarding_completed: Boolean(turn.completed),
        had_agent_reply: Boolean(finalReply),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewResults = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveToApi(messages, true);
      capture(posthog, ONBOARDING_EVENT, {
        step: 'v2_onboarding_completed',
        variant: variantLabel,
        agent_cta: 'view_results',
      });
      navigate('/career-shift-scenario-map');
    } finally {
      setSaving(false);
    }
  };

  const showFinalScreen = Boolean(done && !isCopilotMode);
  const headerFirstName = getFirstNameFromStorage() || 'User';
  const profileCompletionPercent = useMemo(
    () => getMandatoryProfileCompletionPercent(userAnswers),
    [userAnswers]
  );

  const handleHeaderMenuToggle = useCallback(() => {
    setProfileMenuOpen((prev) => !prev);
  }, []);

  const handleHeaderMenuSelect = useCallback((item) => {
    setProfileMenuOpen(false);
    if (item === 'logout') {
      capture(posthog, LOGGED_OUT_EVENT);
      clearIdentifiedSession();
      localStorage.clear();
      window.location.href = '/login';
    }
  }, [posthog]);

  const profileDrawer = useProfileDrawer({
    autoReveal: isCopilotMode,
    initialOpen: typeof window !== 'undefined' && !window.matchMedia(MOBILE_MQ).matches,
    delayInitialOpen: isCopilotMode,
    suppressAutoOpen: holdProfileAutoOpen || showLocationConsent,
    messagesVisible: ready && !loadError && !showTypingDots && !showLocationConsent,
    responseId: showTypingDots ? null : getLastAgentMessageId(messages),
    active: ready && !loadError,
    onToggle: (next) => {
      captureV2(posthog, {
        action: 'mobile_profile_drawer',
        open: next,
        viewport: window.matchMedia(MOBILE_MQ).matches ? 'mobile' : 'desktop',
      });
    },
    onAutoReveal: (trigger, viewport) => {
      captureV2(posthog, { action: 'profile_drawer_auto_reveal', trigger, viewport });
    },
  });
  const profilePanelOpen = profileDrawer.open;

  const handleVoiceEvent = useCallback(
    (action) => {
      captureV2(posthog, { action });
    },
    [posthog, captureV2]
  );

  const pageClassName = cx(
    styles['v2-page'],
    isCopilotMode ? styles['v2-page--embedded'] : styles['v2-page--standalone']
  );
  const shellClassName = cx(
    styles['v2-shell'],
    styles['v2-shell--profile-drawer'],
    isCopilotMode && styles['v2-shell--embedded'],
    showTopAppHeader && styles['v2-shell--with-top-header'],
    profilePanelOpen && styles['v2-shell--mobile-profile-open']
  );

  const topAppHeader = showTopAppHeader ? (
    <TopAppHeader
      firstName={headerFirstName}
      menuOpen={profileMenuOpen}
      onToggleMenu={handleHeaderMenuToggle}
      onMenuSelect={handleHeaderMenuSelect}
    />
  ) : null;

  const profilePanelNode = (
    <ProfileDrawer
      open={profilePanelOpen}
      onClose={profileDrawer.close}
      onToggle={profileDrawer.toggle}
      onHandleTouchStart={profileDrawer.handleTouchStart}
      onHandleTouchEnd={profileDrawer.handleTouchEnd}
      onDrawerTouchStart={profileDrawer.drawerTouchStart}
      onDrawerTouchEnd={profileDrawer.drawerTouchEnd}
    >
      <FinancialProfilePanel
        userAnswers={userAnswers}
        setUserAnswers={setUserAnswers}
        onFinancialProfilePersisted={handleFinancialProfilePersisted}
        showScenarioCta={showScenarioCta}
        onboardingAiDone={onboardingAiDone}
        financialProfileRaw={financialProfileRaw}
      />
    </ProfileDrawer>
  );

  if (!ready) {
    return (
      <div className={pageClassName} ref={pageRef}>
        {topAppHeader}
        <div className={shellClassName}>
          <div className={styles['v2-chat-column']}>
            {topBar}
            <div className={cx(styles['chat-container'], styles['chat-container--v2'])}>
              <V2ChatHeader isCopilotMode={isCopilotMode} profileCompletionPercent={profileCompletionPercent} />
              <div className={styles['messages-container']}>
                {showTypingDots ? (
                  <div className={styles['messages-inner']}>
                    <V2TypingDots messageIndex={0} />
                  </div>
                ) : (
                  <div className={styles['v2-bootstrap-loading']} role="status" aria-live="polite">
                    <V2LoadingPanel />
                  </div>
                )}
              </div>
              <LocationConsentPopup open={showLocationConsent} onChoice={handleLocationConsentChoice} />
            </div>
          </div>
          {profilePanelNode}
        </div>
      </div>
    );
  }

  if (loadError) {
    return <V2ErrorState onBackToLogin={() => navigate('/login')} />;
  }

  return (
    <div className={pageClassName} ref={pageRef}>
      {topAppHeader}
      <div className={shellClassName}>
        <div className={styles['v2-chat-column']}>
          {topBar}
          <div className={cx(styles['chat-container'], styles['chat-container--v2'])}>
            <V2ChatHeader isCopilotMode={isCopilotMode} profileCompletionPercent={profileCompletionPercent} />

            <div className={styles['v2-chat-body']}>
              <div ref={messagesContainerRef} className={styles['messages-container']}>
                <div ref={messagesInnerRef} className={styles['messages-inner']}>
                  <V2MessageList
                    messages={messages}
                    showTypingDots={showTypingDots}
                    showFinalScreen={showFinalScreen}
                    relativeTimeTick={relativeTimeTick}
                    onAgentTypingComplete={handleAgentTypingComplete}
                  />
                </div>
              </div>

              <V2Composer
                ref={composerApiRef}
                composerLocked={composerLocked}
                saving={saving}
                showFinalScreen={showFinalScreen}
                onSubmitText={handleSubmitText}
                onViewResults={handleViewResults}
                onVoiceEvent={handleVoiceEvent}
              />

              <LocationConsentPopup open={showLocationConsent} onChoice={handleLocationConsentChoice} />
            </div>
          </div>
        </div>
        {profilePanelNode}
      </div>
    </div>
  );
}
